export type LocalDate = { readonly year: number; readonly month: number; readonly day: number }

const DAY_MS = 86_400_000

export const formatLocalDate = ({ year, month, day }: LocalDate) =>
  `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

export const parseLocalDate = (date: string) => {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number)
  return { year, month, day } satisfies LocalDate
}

// Memoized: building an Intl.DateTimeFormat is expensive, and a week grid reads one per row per render.
const formatters = new Map<string, Intl.DateTimeFormat>()

const formatterFor = (timeZone: string) => {
  let formatter = formatters.get(timeZone)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric'
    })
    formatters.set(timeZone, formatter)
  }
  return formatter
}

const partsIn = (instant: Date, timeZone: string) => {
  const parts = formatterFor(timeZone).formatToParts(instant)
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find(part => part.type === type)?.value)
  return {
    year: value('year'),
    month: value('month'),
    day: value('day'),
    hour: value('hour'),
    minute: value('minute'),
    second: value('second')
  }
}

const offsetMs = (instant: Date, timeZone: string) => {
  const local = partsIn(instant, timeZone)
  const asUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second)
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000
}

export const localDateIn = (instant: Date, timeZone: string): LocalDate => {
  const { year, month, day } = partsIn(instant, timeZone)
  return { year, month, day }
}

export const localTimeIn = (instant: Date, timeZone: string) => {
  const { hour, minute } = partsIn(instant, timeZone)
  return { hour, minute }
}

export const addLocalDays = ({ year, month, day }: LocalDate, days: number): LocalDate => {
  const shifted = new Date(Date.UTC(year, month - 1, day) + days * DAY_MS)
  return { year: shifted.getUTCFullYear(), month: shifted.getUTCMonth() + 1, day: shifted.getUTCDate() }
}

export const localDaysBetween = (from: LocalDate, to: LocalDate) =>
  Math.round((Date.UTC(to.year, to.month - 1, to.day) - Date.UTC(from.year, from.month - 1, from.day)) / DAY_MS)

// Two passes: the offset at the naive guess can differ from the offset at the answer across a DST change.
export const zonedTime = ({ year, month, day }: LocalDate, hour: number, minute: number, timeZone: string) => {
  const naive = Date.UTC(year, month - 1, day, hour, minute)
  const first = naive - offsetMs(new Date(naive), timeZone)
  return new Date(naive - offsetMs(new Date(first), timeZone))
}

export const zonedWallClock = (instant: Date | string, timeZone: string) => {
  const { year, month, day, hour, minute } = partsIn(new Date(instant), timeZone)
  return `${formatLocalDate({ year, month, day })}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export const instantOfWallClock = (wallClock: string, timeZone: string) => {
  const [hour = 0, minute = 0] = wallClock.slice(11, 16).split(':').map(Number)
  return zonedTime(parseLocalDate(wallClock.slice(0, 10)), hour, minute, timeZone).toISOString()
}
